import { utf8StringToBase64, base64ToBytes } from '../crypto/base64.ts'
import { SOCKET2_DIRECT, resolveSocket2Url, isCapacitorNative } from '../api/ZionServer.ts'

export const DEVICE_LINK_PREFIX = 'DEVICE1::'
export const DEVICE_LINK_TTL_MS = 10 * 60 * 1000

export interface DeviceLinkQrPayload {
  v: 1
  sessionId: string
  pubKey: string
  channelBase: string
  deviceId: string
  exp: number
}

export function deviceSyncChannelBase(): string {
  if (import.meta.env.DEV && !isCapacitorNative()) {
    return resolveSocket2Url('/device-sync')
  }
  return `${SOCKET2_DIRECT}/device-sync`
}

const DEVICE_LINK_APP_URL = import.meta.env.DEV
  ? `${location.origin}/`
  : 'https://www.zionyx.ch/app/'

/** QR corto apribile dalla Fotocamera iPhone → apre la PWA con ?deviceLink= */
export function buildDeviceLinkUrl(sessionId: string): string {
  const sid = sessionId.trim()
  if (!sid) throw new Error('device_link_empty_session')
  try {
    const base =
      typeof location !== 'undefined' ? location.href : 'https://www.zionyx.ch/app/'
    const u = new URL(DEVICE_LINK_APP_URL, base)
    u.searchParams.set('deviceLink', sid)
    return u.toString()
  } catch (err) {
    console.error('[ZION] buildDeviceLinkUrl failed, using fallback', err)
    return `https://www.zionyx.ch/app/?deviceLink=${encodeURIComponent(sid)}`
  }
}

export function encodeDeviceLinkQr(payload: DeviceLinkQrPayload): string {
  return DEVICE_LINK_PREFIX + utf8StringToBase64(JSON.stringify(payload))
}

/** Estrae sessionId da URL ?deviceLink=, payload legacy DEVICE1:: o UUID grezzo. */
export function parseDeviceLinkSessionId(raw: string): string | null {
  const text = raw.trim()
  if (!text) return null

  try {
    if (text.includes('deviceLink=')) {
      const urlText = /^https?:\/\//i.test(text) ? text : `https://local/?${text.replace(/^\?/, '')}`
      const sid = new URL(urlText).searchParams.get('deviceLink')?.trim()
      if (sid) return sid
    }
  } catch {
    /* ignore */
  }

  const legacy = parseDeviceLinkQr(text)
  if (legacy?.sessionId) return legacy.sessionId

  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) {
    return text
  }
  return null
}

export function parseDeviceLinkQr(raw: string): DeviceLinkQrPayload | null {
  const text = raw.trim()
  if (!text.startsWith(DEVICE_LINK_PREFIX)) return null
  try {
    const json = new TextDecoder().decode(base64ToBytes(text.slice(DEVICE_LINK_PREFIX.length)))
    const data = JSON.parse(json) as DeviceLinkQrPayload
    if (data.v !== 1) return null
    if (!data.sessionId?.trim() || !data.pubKey?.trim() || !data.channelBase?.trim()) return null
    if (!data.exp || Date.now() > data.exp) return null
    return data
  } catch {
    return null
  }
}

export function buildReceiverQrPayload(
  sessionId: string,
  ephemeralPubKey: string,
  deviceId: string,
): DeviceLinkQrPayload {
  return {
    v: 1,
    sessionId: sessionId.trim(),
    pubKey: ephemeralPubKey.trim(),
    channelBase: deviceSyncChannelBase(),
    deviceId: deviceId.trim(),
    exp: Date.now() + DEVICE_LINK_TTL_MS,
  }
}
